import { normalizeSatParam } from "../data/goesColor.js";
import { normalizeSatChoice, normalizeSatSector } from "../data/goesGrid.js";

const SHORT_PARAM_TO_PRODUCT = {
  r: "reflectivity",
  v: "velocity",
  sw: "spectrum_width",
  cc: "correlation_coefficient",
  zdr: "differential_reflectivity",
  l3r: "l3_reflectivity",
  l3v: "l3_velocity",
  et: "l3_echo_tops",
  vil: "l3_vil",
  hca: "l3_hydrometeor",
  hhc: "l3_hybrid_hca",
  l3zdr: "l3_zdr",
  l3cc: "l3_cc",
  kdp: "l3_kdp",
};

const PRODUCT_TO_SHORT_PARAM = Object.fromEntries(
  Object.entries(SHORT_PARAM_TO_PRODUCT).map(([k, v]) => [v, k]),
);

export { SHORT_PARAM_TO_PRODUCT, PRODUCT_TO_SHORT_PARAM };

export function parseShareSearch(search) {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  let stationId = params.get("station") || params.get("st");
  let date = params.get("date") || params.get("d");
  let time = params.get("time") || params.get("t");
  let product = params.get("product");
  if (params.get("p")) product = SHORT_PARAM_TO_PRODUCT[params.get("p")] || product;
  return {
    stationId: stationId ? stationId.toUpperCase() : null,
    date: date ? date.replace(/-/g, "") : null,
    time: time ? time.replace(/:/g, "") : null,
    product: product || "reflectivity",
    elev: params.get("elev"),
    live: params.get("live") === "1",
    tracks: params.get("tracks") === "1",
    glm: params.get("glm") === "1",
    ww: params.get("ww") === "1",
    ...readSatParams(params),
  };
}

// sat=<product> turns satellite on. The rest only mean something alongside it:
// satmode=only hides the radar, satsec=m|c|f forces a sector, satgoes=east|west
// forces a satellite, sathold=1 freezes one satellite frame while the radar loops.
export function readSatParams(params) {
  const sat = normalizeSatParam(params.get("sat"));
  return {
    sat,
    satMode: sat && params.get("satmode") === "only" ? "only" : "overlay",
    satSector: sat ? normalizeSatSector(params.get("satsec")) : "auto",
    satGoes: sat ? normalizeSatChoice(params.get("satgoes")) : "auto",
    satHold: !!sat && params.get("sathold") === "1",
  };
}

export function writeSatParams(params, { sat, satMode, satSector, satGoes, satHold } = {}) {
  const product = normalizeSatParam(sat);
  if (!product) return;
  params.set("sat", product);
  if (satMode === "only") params.set("satmode", "only");
  const sector = normalizeSatSector(satSector);
  if (sector !== "auto") params.set("satsec", sector);
  const goes = normalizeSatChoice(satGoes);
  if (goes !== "auto") params.set("satgoes", goes);
  if (satHold && satMode !== "only") params.set("sathold", "1");
}

export function buildShareSearch(state) {
  const params = new URLSearchParams();
  if (state.stationId) params.set("station", state.stationId);
  if (state.date) params.set("date", String(state.date).replace(/-/g, ""));
  if (state.time) params.set("time", String(state.time).replace(/:/g, ""));
  if (state.product && state.product !== "reflectivity") params.set("product", state.product);
  if (state.elev != null) params.set("elev", Number(state.elev).toFixed(1));
  if (state.live) params.set("live", "1");
  if (state.tracks) params.set("tracks", "1");
  if (state.glm) params.set("glm", "1");
  if (state.ww) params.set("ww", "1");
  writeSatParams(params, state);
  return params.toString();
}


// Rain windows are UTC YYYYMMDDHHMM-YYYYMMDDHHMM. A pinned point is rpt=lat,lon.
export function formatRainUrlTime(ms) {
  return new Date(ms).toISOString().slice(0, 16).replace(/[-:T]/g, "");
}

export function writeRainParams(params, rain) {
  if (!rain || !(rain.endMs > rain.startMs)) return;
  params.set("rain", `${formatRainUrlTime(rain.startMs)}-${formatRainUrlTime(rain.endMs)}`);
  if (rain.point && Number.isFinite(rain.point.lat) && Number.isFinite(rain.point.lon)) {
    params.set("rpt", `${Number(rain.point.lat).toFixed(4)},${Number(rain.point.lon).toFixed(4)}`);
  }
}

export function parseRainUrl(rainParam, pointParam) {
  const m = String(rainParam || "").match(/^(\d{12})-(\d{12})$/);
  if (!m) return null;
  const toMs = (v) => Date.UTC(+v.slice(0, 4), +v.slice(4, 6) - 1, +v.slice(6, 8), +v.slice(8, 10), +v.slice(10, 12));
  const startMs = toMs(m[1]);
  const endMs = toMs(m[2]);
  if (!(endMs > startMs)) return null;
  const [lat, lon] = String(pointParam || "").split(",").map(Number);
  const point = Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
  return { startMs, endMs, point };
}

// Bit 7 of the v2 short-token flags. Payload is appended after the map bytes:
// u32 start minute, u16 duration minutes, u24 lat (or 0xffffff if no point), u24 lon.
export const RAIN_TOKEN_FLAG = 128;

function pushU16(bytes, v) {
  const n = Math.max(0, Math.min(0xffff, Math.round(v)));
  bytes.push((n >> 8) & 255, n & 255);
}

function pushU24(bytes, v) {
  const n = Math.max(0, Math.min(0xffffff, Math.round(v)));
  bytes.push((n >> 16) & 255, (n >> 8) & 255, n & 255);
}

function pushU32(bytes, v) {
  const n = Math.max(0, Math.min(0xffffffff, Math.round(v))) >>> 0;
  bytes.push((n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255);
}

function readU16(bytes, p) {
  return (bytes[p] << 8) | bytes[p + 1];
}

function readU24(bytes, p) {
  return (bytes[p] << 16) | (bytes[p + 1] << 8) | bytes[p + 2];
}

function readU32(bytes, p) {
  return bytes[p] * 16777216 + bytes[p + 1] * 65536 + bytes[p + 2] * 256 + bytes[p + 3];
}

export function appendRainToken(bytes, rain) {
  const startMin = Math.round(rain.startMs / 60000);
  const durationMin = Math.max(1, Math.round((rain.endMs - rain.startMs) / 60000));
  pushU32(bytes, startMin);
  pushU16(bytes, durationMin);
  const point = rain.point;
  if (point && Number.isFinite(point.lat) && Number.isFinite(point.lon)) {
    pushU24(bytes, Math.round((point.lat + 90) * 10000));
    pushU24(bytes, Math.round((point.lon + 180) * 10000));
  } else {
    pushU24(bytes, 0xffffff);
  }
  return bytes;
}

export function readRainToken(bytes, offset) {
  if (offset + 9 > bytes.length) return null;
  const startMin = readU32(bytes, offset);
  const durationMin = readU16(bytes, offset + 4);
  const latQ = readU24(bytes, offset + 6);
  let next = offset + 9;
  let point = null;
  if (latQ !== 0xffffff) {
    if (next + 3 > bytes.length) return null;
    const lonQ = readU24(bytes, next);
    next += 3;
    point = {
      lat: Number((latQ / 10000 - 90).toFixed(4)),
      lon: Number((lonQ / 10000 - 180).toFixed(4)),
    };
  }
  if (!(durationMin > 0)) return null;
  return {
    rain: { startMs: startMin * 60000, endMs: (startMin + durationMin) * 60000, point },
    offset: next,
  };
}

export function rainFromToken(flags, bytes, offset) {
  if ((flags & RAIN_TOKEN_FLAG) === 0) return null;
  return readRainToken(bytes, offset);
}
